import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getTemplateComponent, getDefaultTemplate } from '../templates';
import buildBlobUrl from '../utils/buildBlobUrl';
import { getToken } from '../utils/auth';
import './DocumentGenerator.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Field definitions for each document type
 */
const DOC_FIELDS = {
  SLC: [
    { key: 'Certificate_Number', label: 'Certificate Number' },
    { key: 'Book_Number', label: 'Book Number' },
    { key: 'Student_Name', label: 'Student Name', auto: true },
    { key: 'Father_Name', label: "Father's Name", auto: true },
    { key: 'Mother_Name', label: "Mother's Name", auto: true },
    { key: 'Guardian_Name', label: 'Guardian Name' },
    { key: 'Date_Of_Birth', label: 'Date of Birth', type: 'date', auto: true },
    { key: 'Date_Of_Birth_Words', label: 'D.O.B (Words)' },
    { key: 'Gender', label: 'Gender', auto: true, options: ['Male', 'Female', 'Other'] },
    { key: 'Nationality', label: 'Nationality', default: 'Indian' },
    { key: 'Religion', label: 'Religion' },
    { key: 'Category', label: 'Category', options: ['General', 'SC', 'ST', 'OBC', 'EWS'] },
    { key: 'Admission_Number', label: 'Admission No', auto: true },
    { key: 'Date_Of_First_Admission', label: 'Date of First Admission', type: 'date' },
    { key: 'Class_First_Admitted', label: 'Class First Admitted' },
    { key: 'Class_Last_Studied', label: 'Class Last Studied', auto: true },
    { key: 'Section_Last_Studied', label: 'Section', auto: true },
    { key: 'Subjects_Studied', label: 'Subjects Studied', wide: true },
    { key: 'Qualified_For_Promotion', label: 'Qualified for Promotion', options: ['Yes', 'No'] },
    { key: 'Exam_Result', label: 'Exam Result', options: ['Passed', 'Promoted', 'Detained'] },
    { key: 'Total_Working_Days', label: 'Total Working Days', type: 'number' },
    { key: 'Total_Days_Attended', label: 'Total Days Attended', type: 'number' },
    { key: 'Conduct', label: 'Conduct', default: 'Good', options: ['Good', 'Very Good', 'Excellent', 'Satisfactory'] },
    { key: 'Date_Of_Leaving', label: 'Date of Leaving', type: 'date' },
    { key: 'Reason_For_Leaving', label: 'Reason for Leaving', wide: true },
    { key: 'All_Dues_Cleared', label: 'All Dues Cleared', default: 'Yes', options: ['Yes', 'No'] },
    { key: 'Extra_Curricular', label: 'Extra-Curricular', wide: true },
    { key: 'Remarks', label: 'Remarks', wide: true, multiline: true },
    { key: 'Certificate_Date', label: 'Certificate Date', type: 'date', default: 'today' },
  ],
  TC: [
    { key: 'Certificate_Number', label: 'Certificate Number' },
    { key: 'Book_Number', label: 'Book Number' },
    { key: 'Admission_Number', label: 'Admission No', auto: true },
    { key: 'Student_Name', label: 'Student Name', auto: true },
    { key: 'Father_Name', label: "Father's Name", auto: true },
    { key: 'Mother_Name', label: "Mother's Name", auto: true },
    { key: 'Guardian_Name', label: 'Guardian Name' },
    { key: 'Date_Of_Birth', label: 'Date of Birth', type: 'date', auto: true },
    { key: 'Date_Of_Birth_Words', label: 'D.O.B (Words)', wide: true },
    { key: 'Gender', label: 'Gender', auto: true, options: ['Male', 'Female', 'Other'] },
    { key: 'Nationality', label: 'Nationality', default: 'Indian' },
    { key: 'Religion', label: 'Religion' },
    { key: 'Caste', label: 'Caste' },
    { key: 'Category', label: 'Category', options: ['General', 'SC', 'ST', 'OBC', 'EWS'] },
    { key: 'Aadhaar_Number', label: 'Aadhaar Number' },
    { key: 'PEN', label: 'PEN' },
    { key: 'Address', label: 'Address', auto: true, wide: true },
    { key: 'Date_Of_First_Admission', label: 'Date of First Admission', type: 'date' },
    { key: 'Class_First_Admitted', label: 'Class First Admitted' },
    { key: 'Class_Last_Studied', label: 'Class Last Studied', auto: true },
    { key: 'Section_Last_Studied', label: 'Section', auto: true },
    { key: 'Academic_Session', label: 'Academic Session' },
    { key: 'School_Board', label: 'School Board' },
    { key: 'Medium_Of_Instruction', label: 'Medium of Instruction' },
    { key: 'Subjects_Studied', label: 'Subjects Studied', wide: true },
    { key: 'Qualified_For_Promotion', label: 'Qualified', options: ['Yes', 'No'] },
    { key: 'Exam_Result', label: 'Exam Result', options: ['Passed', 'Promoted', 'Detained'] },
    { key: 'Total_Working_Days', label: 'Working Days', type: 'number' },
    { key: 'Total_Days_Attended', label: 'Days Attended', type: 'number' },
    { key: 'Conduct', label: 'Conduct', default: 'Good', options: ['Good', 'Very Good', 'Excellent', 'Satisfactory'] },
    { key: 'Date_Of_Leaving', label: 'Date of Leaving', type: 'date' },
    { key: 'Reason_For_Leaving', label: 'Reason for Leaving', wide: true },
    { key: 'All_Dues_Cleared', label: 'All Dues Cleared', default: 'Yes', options: ['Yes', 'No'] },
    { key: 'TC_Issue_Date', label: 'TC Issue Date', type: 'date' },
    { key: 'Remarks', label: 'Remarks', wide: true, multiline: true },
    { key: 'Certificate_Date', label: 'Certificate Date', type: 'date', default: 'today' },
  ],
  BONAFIDE: [
    { key: 'Certificate_Number', label: 'Certificate Number' },
    { key: 'Student_Name', label: 'Student Name', auto: true },
    { key: 'Father_Name', label: "Father's Name", auto: true },
    { key: 'Mother_Name', label: "Mother's Name", auto: true },
    { key: 'Student_Class', label: 'Class', auto: true },
    { key: 'Student_Section', label: 'Section', auto: true },
    { key: 'Admission_Number', label: 'Admission No', auto: true },
    { key: 'Academic_Session', label: 'Academic Session' },
    { key: 'Purpose', label: 'Purpose', wide: true },
    { key: 'Remarks', label: 'Remarks', wide: true, multiline: true },
    { key: 'Certificate_Date', label: 'Certificate Date', type: 'date', default: 'today' },
  ],
};

/**
 * Map student record fields to certificate form fields
 */
function autoFillFromStudent(student, docType) {
  const map = {
    Student_Name: student.Student_Name,
    Father_Name: student.Student_Father,
    Mother_Name: student.Student_Mother,
    Date_Of_Birth: student.Student_DOB,
    Gender: student.Student_Gender,
    Admission_Number: student.Admission_Number,
    Class_Last_Studied: student.Student_Class,
    Section_Last_Studied: student.Student_Section,
    Address: student.Student_Address,
    // Bonafide uses different key names
    Student_Class: student.Student_Class,
    Student_Section: student.Student_Section,
  };
  return map;
}

const DocumentGenerator = ({ docType, templateId }) => {
  const { schoolId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [existingCert, setExistingCert] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const printRef = useRef(null);

  const token = getToken(schoolId);
  const headers = { Authorization: `Bearer ${token}` };
  const fields = DOC_FIELDS[docType] || [];

  // Initialize form with defaults
  useEffect(() => {
    const init = {};
    fields.forEach(f => {
      if (f.default === 'today') init[f.key] = new Date().toISOString().split('T')[0];
      else if (f.default) init[f.key] = f.default;
      else init[f.key] = '';
    });
    setForm(init);
  }, [docType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load school info
  useEffect(() => {
    fetch(`${API_URL}/school/info`, { headers })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          const info = d.data;
          if (info.schoolLogo) info.schoolLogo = buildBlobUrl(info.schoolLogo);
          setSchoolInfo(info);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Search students
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearchLoading(true);
    try {
      const res = await fetch(`${API_URL}/students/search?q=${encodeURIComponent(searchQuery.trim())}`, { headers });
      const data = await res.json();
      if (data.success) setSearchResults(data.data || []);
    } catch {
      // ignore
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Select student - auto-fill & check existing cert
  const selectStudent = useCallback(async (student) => {
    setSelectedStudent(student);
    setSearchResults([]);
    setSearchQuery('');
    setAlert(null);

    // Auto-fill form from student data
    const autoData = autoFillFromStudent(student, docType);
    setForm(prev => {
      const updated = { ...prev };
      fields.forEach(f => {
        if (f.auto && autoData[f.key]) {
          updated[f.key] = autoData[f.key];
        }
      });
      return updated;
    });

    // Check for existing certificate
    try {
      const certType = docType === 'BONAFIDE' ? 'bonafide' : docType.toLowerCase();
      const res = await fetch(`${API_URL}/certificates/${certType}/student/${student.Student_Id}`, { headers });
      const data = await res.json();
      if (data.success && data.data) {
        setExistingCert(data.data);
        setIsEditing(true);
        // Fill form with existing cert data
        setForm(prev => {
          const updated = { ...prev };
          fields.forEach(f => {
            if (data.data[f.key] !== undefined && data.data[f.key] !== null) {
              updated[f.key] = data.data[f.key];
            }
          });
          return updated;
        });
        setAlert({ type: 'info', msg: 'Existing certificate found. You can edit and update.' });
      } else {
        setExistingCert(null);
        setIsEditing(false);
      }
    } catch {
      // ignore
    }
  }, [docType, fields]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save / Update
  const handleSave = async () => {
    if (!selectedStudent) {
      setAlert({ type: 'error', msg: 'Please select a student first' });
      return;
    }
    setSaving(true);
    setAlert(null);
    try {
      const certType = docType === 'BONAFIDE' ? 'bonafide' : docType.toLowerCase();
      const body = { ...form, studentId: selectedStudent.Student_Id };
      let res;

      if (isEditing && existingCert) {
        res = await fetch(`${API_URL}/certificates/${certType}/${existingCert.Certificate_Id}`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API_URL}/certificates/${certType}`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }

      const data = await res.json();
      if (data.success) {
        setExistingCert(data.data);
        setIsEditing(true);
        setAlert({ type: 'success', msg: isEditing ? 'Certificate updated!' : 'Certificate created!' });
      } else {
        setAlert({ type: 'error', msg: data.message || 'Failed to save' });
      }
    } catch {
      setAlert({ type: 'error', msg: 'Failed to save certificate' });
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!existingCert) return;
    if (!window.confirm('Delete this certificate?')) return;
    try {
      const certType = docType === 'BONAFIDE' ? 'bonafide' : docType.toLowerCase();
      const res = await fetch(`${API_URL}/certificates/${certType}/${existingCert.Certificate_Id}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (data.success) {
        setExistingCert(null);
        setIsEditing(false);
        setAlert({ type: 'success', msg: 'Certificate deleted' });
      }
    } catch {
      setAlert({ type: 'error', msg: 'Failed to delete' });
    }
  };

  // Print — full A4, white background, colors preserved, single page
  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');

    const printStyles = `
      <style>
        @page {
          size: A4 portrait;
          margin: 0;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
          box-sizing: border-box;
        }
        html, body {
          margin: 0;
          padding: 0;
          width: 210mm;
          min-height: 297mm;
          background: #fff !important;
          color: #000;
          font-family: 'Arial', sans-serif;
        }
        body {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 0;
        }
        .tpl-page {
          width: 210mm !important;
          min-height: 297mm !important;
          max-height: 297mm;
          padding: 0 !important;
          margin: 0 !important;
          background: #fff !important;
          box-shadow: none !important;
          overflow: hidden;
          page-break-after: avoid;
          page-break-inside: avoid;
        }
        .tpl-border {
          min-height: calc(297mm - 34mm) !important;
          overflow: hidden;
        }
        /* Hide dark theme artifacts */
        .docgen-preview-scroll {
          background: none !important;
          border: none !important;
          padding: 0 !important;
        }
      </style>
    `;

    printWindow.document.write(`<html><head><meta charset="utf-8">${printStyles}</head><body>${printRef.current.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };

  // Reset
  const handleReset = () => {
    setSelectedStudent(null);
    setExistingCert(null);
    setIsEditing(false);
    setAlert(null);
    const init = {};
    fields.forEach(f => {
      if (f.default === 'today') init[f.key] = new Date().toISOString().split('T')[0];
      else if (f.default) init[f.key] = f.default;
      else init[f.key] = '';
    });
    setForm(init);
  };

  // Get template component
  const TemplateComponent = templateId
    ? getTemplateComponent(templateId)
    : getDefaultTemplate(docType);

  // Build template data from form
  const templateData = { ...form, ...(existingCert || {}) };
  // Override with form values (form takes precedence for live preview)
  fields.forEach(f => {
    if (form[f.key] !== undefined && form[f.key] !== '') {
      templateData[f.key] = form[f.key];
    }
  });

  return (
    <div className="docgen">
      {/* Alert */}
      {alert && (
        <div className={`docgen-alert docgen-alert-${alert.type}`}>
          <span>{alert.msg}</span>
          <button onClick={() => setAlert(null)}>&times;</button>
        </div>
      )}

      <div className="docgen-layout">
        {/* Left: Form */}
        <div className="docgen-form-panel">
          {/* Student Search */}
          <div className="docgen-section">
            <h3 className="docgen-section-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Search Student
            </h3>
            <div className="docgen-search-row">
              <input
                type="text"
                className="docgen-input"
                placeholder="Search by name, ID, or admission no..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
              />
              <button className="docgen-btn docgen-btn-primary" onClick={handleSearch} disabled={searchLoading}>
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="docgen-search-results">
                {searchResults.map(s => (
                  <div key={s.Student_Id} className="docgen-search-item" onClick={() => selectStudent(s)}>
                    <div className="docgen-search-info">
                      <strong>{s.Student_Name}</strong>
                      <span>{s.Student_Id} | Class {s.Student_Class}-{s.Student_Section}</span>
                    </div>
                    <span className="docgen-search-adm">{s.Admission_Number || ''}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Selected Student */}
            {selectedStudent && (
              <div className="docgen-selected-student">
                <div>
                  <strong>{selectedStudent.Student_Name}</strong>
                  <span>{selectedStudent.Student_Id} | Class {selectedStudent.Student_Class}-{selectedStudent.Student_Section}</span>
                </div>
                <button className="docgen-btn-sm" onClick={handleReset}>Change</button>
              </div>
            )}
          </div>

          {/* Form Fields */}
          {selectedStudent && (
            <div className="docgen-section">
              <h3 className="docgen-section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Certificate Details
                {isEditing && <span className="docgen-edit-badge">Editing</span>}
              </h3>

              <div className="docgen-fields-grid">
                {fields.map(field => (
                  <div key={field.key} className={`docgen-field ${field.wide ? 'docgen-field-wide' : ''}`}>
                    <label>
                      {field.label}
                      {field.auto && <span className="docgen-auto-tag">auto</span>}
                    </label>
                    {field.options ? (
                      <select
                        className="docgen-input"
                        value={form[field.key] || ''}
                        onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        <option value="">Select...</option>
                        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : field.multiline ? (
                      <textarea
                        className="docgen-input docgen-textarea"
                        rows={3}
                        value={form[field.key] || ''}
                        onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    ) : (
                      <input
                        type={field.type || 'text'}
                        className="docgen-input"
                        value={form[field.key] || ''}
                        onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="docgen-actions">
                <button className="docgen-btn docgen-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : isEditing ? 'Update Certificate' : 'Save Certificate'}
                </button>
                {isEditing && (
                  <button className="docgen-btn docgen-btn-danger" onClick={handleDelete}>Delete</button>
                )}
                <button className="docgen-btn docgen-btn-secondary" onClick={handlePrint}>
                  Print
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Template Preview */}
        <div className="docgen-preview-panel">
          <h3 className="docgen-section-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
            </svg>
            Live Preview
            {templateId && <span className="docgen-template-tag">{templateId}</span>}
          </h3>
          <div className="docgen-preview-scroll" ref={printRef}>
            {TemplateComponent ? (
              <TemplateComponent data={templateData} schoolInfo={schoolInfo} />
            ) : (
              <div className="docgen-no-template">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <p>No template selected. Go to Settings to assign a template.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentGenerator;
